/**
 * AppDialog — the ONE shared New/Edit popup for every form (standing rule 12).
 *
 * Faithful to the MDL Study `.modal` standard: 460px default (right-sized — set
 * `width` to hug a form's controls), grey `#f0f0f0` header, 6px radius,
 * `rgba(0,0,0,.45)` backdrop, z-index 1500, body scrolls internally past 92vh.
 *
 * Forms pass only their title + body + footer; they NEVER style the overlay,
 * header, backdrop or sizing themselves.  Retune appDialog.css once and every
 * popup in the app — current and future — changes together.
 *
 * @example
 * <AppDialog open={open} title="New Method" icon="ti-flask" onClose={close}
 *   footer={<><button className="ca-btn" onClick={close}>Cancel</button>
 *            <button className="ca-btn ca-btn-solid" onClick={save}>Save</button></>}>
 *   ...form fields...
 * </AppDialog>
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAutoFocusFirstControl } from '../../hooks/useAutoFocusFirstControl';
import { useDialogSpatialNav } from '../../hooks/useDialogSpatialNav';
import { parseCombo, comboMatchesEvent, comboAllowsInput, isEditableTarget } from '../hotkeys/keyCombo';
import './appDialog.css';

/** A dialog-local keyboard shortcut. The global hotkey store yields to any open
 *  modal, so a dialog's own action keys are wired here instead (like Esc / Enter /
 *  Ctrl+S below). Show the key in the matching button's tooltip for discoverability. */
export interface DialogHotkey {
  /** Combo string, e.g. "alt+p" (same syntax as useHotkeys). */
  combo: string;
  /** Action to run. */
  run: () => void;
  /** When false the binding is inert (mirrors a disabled button). */
  enabled?: boolean;
}

export interface AppDialogProps {
  /** Whether the dialog is shown.  When false the component renders nothing. */
  open: boolean;
  /** Header title text (left of the close ✕). */
  title: ReactNode;
  /** Optional Tabler icon class for the header (e.g. "ti-flask"). */
  icon?: string;
  /** Body content — the form fields.  Hugs its content; scrolls past 92vh. */
  children: ReactNode;
  /** Footer content — typically the Cancel / Save buttons. Omit for none. */
  footer?: ReactNode;
  /** Dialog width in px (default 460 — size it to FIT the controls). */
  width?: number;
  /** Close when the backdrop is clicked (default true). */
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed (default true). */
  closeOnEsc?: boolean;
  /** Called when the user closes via ✕, backdrop, or Escape. */
  onClose: () => void;
  /** Extra class on the OVERLAY, for a form that themes its own popup
   *  (e.g. Sample Registration's accent header via `.sr-dlg`).  Use this
   *  instead of wrapping `<AppDialog>` in a styling div — a wrapper would sit
   *  in the page's layout flow and, under `.content > * { flex: 1 }`, steal
   *  half the page height from the list behind it. */
  className?: string;
  /** Show a Maximize / Restore button (before the close ✕) that toggles the modal
   *  to near-full-screen — GoLive/XAF popup window chrome. Opt-in per dialog. */
  maximizable?: boolean;
  /** Pressing Enter (when focus is NOT in a textarea / select / button / link)
   *  activates the footer's primary button (`.ca-btn-solid`) — the popup's Save /
   *  OK (CLAUDE.md standing rule 14, §18.6).  Default true; set false for a dialog
   *  whose Enter must do something else. Esc already closes (closeOnEsc). */
  submitOnEnter?: boolean;
  /** Dialog-local action shortcuts (e.g. a Report / New button inside the popup).
   *  The global store yields to modals, so these are wired by the dialog itself. */
  hotkeys?: DialogHotkey[];
}

export function AppDialog({
  open, title, icon, children, footer,
  width = 460, closeOnBackdrop = true, closeOnEsc = true, onClose, className,
  maximizable = false, submitOnEnter = true, hotkeys,
}: AppDialogProps) {
  const [max, setMax] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Focus restoration (mouse-free operation, ADR-0013): remember what had focus
  // when the dialog OPENED (e.g. the grid row that launched it) and return focus
  // there when it CLOSES, so keyboard navigation continues where it left off
  // instead of dropping to the page top. preventScroll avoids a jump; the guard
  // skips a trigger that was removed while the dialog was open.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const restoreFallbackRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    restoreFocusRef.current = opener;
    // A stable focusable ancestor to fall back to when the exact opener is
    // re-rendered away while the dialog is open — e.g. a grid CELL button (BottleID /
    // Parameter) whose row data changed on OK, so the button node is replaced and
    // focus would otherwise drop to <body>. The grid ROW keeps its identity (stable
    // key), so focus lands back on it and keyboard nav resumes (user report 2026-09-22).
    restoreFallbackRef.current = opener?.closest<HTMLElement>('tr[tabindex]') ?? null;
    return () => {
      const el = restoreFocusRef.current;
      const fb = restoreFallbackRef.current;
      restoreFocusRef.current = null;
      restoreFallbackRef.current = null;
      if (el && el.isConnected && typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
      } else if (fb && fb.isConnected && typeof fb.focus === 'function') {
        fb.focus({ preventScroll: true });
      }
    };
  }, [open]);

  // Focus the dialog's first usable field on open (mouse-free, ADR-0013). Declared
  // AFTER the restore-focus effect so that effect captures the launcher FIRST, then
  // this moves focus into the dialog — the two compose (open → first field; close →
  // focus returns to the launcher).
  useAutoFocusFirstControl(bodyRef, open);

  // Arrow-key spatial navigation INSIDE the dialog (mouse-free, ADR-0013). The main
  // nav (AppShell) yields to any open modal, so without this arrows are dead in every
  // popup — you could only Tab (user report 2026-09-22). Scoped to the modal so focus
  // never leaves it; purely additive.
  useDialogSpatialNav(modalRef, open);

  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  // Enter-to-submit (standing rule 14): activate the footer's primary button.
  // The global hotkey store yields to modals (aria-modal), so a dialog owns its
  // own Enter — this gives every AppDialog's Save/OK a key with no per-dialog code.
  useEffect(() => {
    if (!open || !submitOnEnter) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.defaultPrevented || e.isComposing) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      // Let the user's own control handle Enter (newlines, button/link activation,
      // native select), and skip a custom editable region.
      if (tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') return;
      if (t?.isContentEditable || t?.closest('[data-hotkeys-ignore]')) return;
      const primary = modalRef.current?.querySelector<HTMLButtonElement>('.ad-ftr .ca-btn-solid:not(:disabled)');
      if (primary) { e.preventDefault(); primary.click(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitOnEnter]);

  // Ctrl/⌘+S saves (standing rule 14): activate the footer's primary (Save / OK) even
  // while a field is focused — the global store yields to this aria-modal, so a popup
  // must own its own Ctrl+S. Separate from Enter (works regardless of submitOnEnter)
  // and preventDefault suppresses the browser's Save-page dialog (user report 2026-09-21).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
        const primary = modalRef.current?.querySelector<HTMLButtonElement>('.ad-ftr .ca-btn-solid:not(:disabled)');
        if (primary) { e.preventDefault(); primary.click(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Dialog-local action shortcuts (standing rule 14). The global hotkey store yields
  // to any open modal, so a popup's own buttons (e.g. SCC Report / New) get their keys
  // here — same scoped-listener mechanism as Enter / Ctrl+S above, reusing the shared
  // combo matcher so syntax + editable-target rules match useHotkeys exactly.
  useEffect(() => {
    if (!open || !hotkeys || hotkeys.length === 0) return;
    const parsed = hotkeys.map((h) => ({ ...h, c: parseCombo(h.combo) }));
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || e.repeat) return;
      for (const h of parsed) {
        if (h.enabled === false) continue;
        if (!comboMatchesEvent(h.c, e)) continue;
        if (isEditableTarget(e.target) && !comboAllowsInput(h.c)) continue; // plain key never fires mid-typing
        e.preventDefault();
        h.run();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hotkeys]);

  // Always reopen at normal size.
  useEffect(() => { if (!open) setMax(false); }, [open]);

  // Maximize fills the shell CONTENT AREA (right of the rail + sidebar, below the
  // top bar) — NOT the whole viewport — matching the grid / detail-view maximize
  // (user request 2026-08-31). The dialog is PORTALED to <body>, so it can't use
  // `inset:0` inside `.content`; instead we measure the live `.content` rect and
  // pin the overlay to it (robust to sidebar collapse / responsive). Falls back to
  // near-full-viewport if `.content` isn't present.
  const [contentBox, setContentBox] = useState<
    { top: number; left: number; width: number; height: number } | null>(null);
  useEffect(() => {
    if (!max) { setContentBox(null); return; }
    const measure = () => {
      const el = document.querySelector('.content') as HTMLElement | null;
      if (!el) { setContentBox(null); return; }
      const r = el.getBoundingClientRect();
      setContentBox({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [max]);

  if (!open) return null;

  // PORTALED to <body>, never rendered inline in the page (fix 2026-07-29).
  // A page returns `<>{page}{dialog}</>`, so an inline dialog becomes a SIBLING
  // of the page inside the shell's `.content` — and `.content > * { flex: 1 }`
  // (shell.css) then splits the height EQUALLY between them.  The visible bug:
  // opening Sample Registration's "Registration Options" collapsed the list
  // behind it from ~16 rows to 4.  A portal takes the dialog out of every
  // page's layout flow, so no popup can ever resize the form behind it.
  return createPortal(
    <div
      className={`ad-overlay${className ? ` ${className}` : ''}${max && !contentBox ? ' ad-overlay-max' : ''}`}
      style={max && contentBox
        ? { position: 'fixed', top: contentBox.top, left: contentBox.left,
            width: contentBox.width, height: contentBox.height,
            right: 'auto', bottom: 'auto', padding: 0,
            alignItems: 'stretch', justifyContent: 'stretch' }
        : undefined}
      onMouseDown={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        className={`ad-modal${max ? ' ad-maximized' : ''}`}
        style={max
          ? (contentBox
              ? { width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none' }
              : { width: '96vw', height: '92vh', maxHeight: '92vh' })
          : { width }}
        role="dialog"
        aria-modal="true"
      >
        <div className="ad-hdr">
          <span className="ad-title">
            {icon && <i className={`ti ${icon}`} aria-hidden="true" />} {title}
          </span>
          <span className="ad-hdr-btns">
            {maximizable && (
              <button className="ad-max" onClick={() => setMax((m) => !m)}
                      aria-label={max ? 'Restore' : 'Maximize'} title={max ? 'Restore' : 'Maximize'}>
                <i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} aria-hidden="true" />
              </button>
            )}
            <button className="ad-close" onClick={onClose} aria-label="Close" title="Close">
              ✕
            </button>
          </span>
        </div>
        <div ref={bodyRef} className="ad-body">{children}</div>
        {footer && <div className="ad-ftr">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
